import React, { useEffect, useState, useRef, useCallback } from 'react';
import styled from 'styled-components';
import LinePlot from '../../../inference/recommend/widgets/components/WinrateGraphs';
import { NewHistoryRecord } from '../../../inference/recommend/type';
import {
  DetailTable,
  TeamMetaChart,
} from '../../../inference/recommend/component/stage3/MetadataTable';
import { set, toJS } from 'mobx';
import { observer } from 'mobx-react';
import { LoadingSpinner } from '../../../inference/recommend/component/stage2/loadingSpinner';
import { getHistory } from '../../../../apis/historyApis';
import { useTranslation } from 'react-i18next';

const RecordContainer = styled.div<{ showDetails?: boolean }>`
  display: flex;
  flex-direction: column;

  border: 1px solid #c7c7c7;
  box-shadow: 0px 0px 6px 2px rgba(0, 0, 0, 0.25);
  border-radius: 16px;
  margin-bottom: 1rem;
  padding: 20px 40px;
  transition: max-height 0.3s ease-in-out, padding 3s ease-in-out;
  overflow: hidden;

  width: 90%;
`;

const TeamContainer = styled.div<{ showDetails?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;
`;

const ChampionImage = styled.img`
  width: 100%;
  height: auto;
`;

const TeamSection = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  padding: 20px 0;
  justify-content: space-between;
  align-items: center;
`;

const DateText = styled.div`
  font-size: 1.2rem;
  color: #888;
`;

const PlayerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  flex-grow: 1;
  max-width: calc(20%);
`;

const TitleText = styled.div`
  font-size: 1.7rem;
  font-weight: bold;
  padding: 0rem 1rem;
  border-right: 1px solid #888;
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  vertical-align: middle;
  gap: 10px;
  margin-bottom: 1rem;
`;

const PlayerCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const PlayerImage = styled.div<{ url: string }>`
  width: 50%;
  padding-bottom: 50%;
  border-radius: 50%;
  border: 1px solid #ccc;
  box-sizing: border-box;
  background-image: url(${({ url }) => url});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`;

const PlayerName = styled.div`
  font-family: 'Pretendard';
  font-size: 1rem;
  font-weight: 200;
  text-align: center;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 300px) {
    font-size: 0.8rem;
  }
  @media (max-width: 200px) {
    font-size: 0.6rem;
  }
`;
const BanDeatilSection = styled.div`
  display: flex;
  width: 100%;
  height: fit-content;

  margin: 1rem 0;
  border-radius: 10px;
  align-self: center;

  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: #060611;

  gap: 35px;
  padding: 1.5rem;
  box-sizing: border-box;
`;
const BorderDiv = styled.div`
  width: 100%;
  height: 5px;
  border-radius: 2.5px;
  background: rgba(0, 0, 0, 0.1);
  border: 0.1px solid #ffffff;
`;

const WindRatesContainer = styled.div`
  display: flex;
  width: 30vw; /* 반응형 너비 설정 */
  flex-direction: row;
  align-items: center;
  justify-content: center;
  margin: 1rem 0;
`;

const CenterVersusText = styled.div`
  font-size: 1.7vw;
  font-weight: bold;
  margin: 0 0.5rem;
  color: black;
`;

const WinRate = styled.div<{ isRed: boolean }>`
  font-family: 'Beaufort for LOL';
  font-style: normal;
  font-size: 2vw;
  font-weight: 900;
  color: ${({ isRed }) => (isRed ? '#AF2A00' : '#0089AF')};
`;

const FilterContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
  margin-bottom: 1rem;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 1rem;
`;

const LoadMoreButton = styled.button`
  background-color: #007bff;
  color: white;
  font-size: 1rem;
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  &:hover {
    background-color: #0056b3;
  }
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

interface BanpickRecordProps {
  record?: NewHistoryRecord;
}

const ScrollBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 90vh;
`;

const OrderTitle = styled.div`
  font-family: 'Pretendard';
  font-style: normal;
  font-weight: bold;
  font-size: 1.5rem;
  color: #ffffff;
  text-align: center;
`;

const BanpickRecordItem: React.FC<BanpickRecordProps> = () => {
  const { t } = useTranslation();
  const [recordLists, setRecordLists] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  // ✅ API 호출 함수 (페이징 적용)
  const fetchRecords = useCallback(
    async (pageNum = 1, reset = false) => {
      if (loading) return;
      setLoading(true);

      try {
        const params = { page: pageNum, size: 10, startDate, endDate };
        const res = await getHistory(params);
        const newRecords = res.data || [];

        if (reset) {
          setRecordLists(newRecords);
          setPage(2);
        } else {
          setRecordLists((prev) => [...prev, ...newRecords]);
          setPage((prevPage) => prevPage + 1);
        }

        setHasMore(newRecords.length > 0);
      } catch (error) {
        console.error(error);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [loading, startDate, endDate]
  );

  // ✅ 최초 실행 (1회만 실행)
  useEffect(() => {
    fetchRecords(1, true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ✅ 날짜 필터 변경 시 데이터 리셋
  useEffect(() => {
    if (startDate && endDate) {
      fetchRecords(1, true);
    }
  }, [startDate, endDate]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ width: '100%', height: '100%' }}>
      {!loading ? (
        <FilterContainer>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </FilterContainer>
      ) : (
        ''
      )}
      <ScrollBox>
        {!loading && recordLists.length !== 0 ? (
          recordLists?.map((recordblob: any, index: number) => {
            const record = recordblob.result;

            let redbanpickTotalScore = 0;
            let bluebanpickTotalScore = 0;

            let redSta = 0;
            let blueSta = 0;

            if (!record) {
            } else {
              const redbanwinOrderRev = [
                record.graphData[7],
                record.graphData[8],
                record.graphData[11],
                record.graphData[17],
                record.graphData[18],
              ].map((turn: any) => {
                if (turn) {
                  return turn.winrate;
                }
                return 0;
              });
              redbanpickTotalScore =
                redbanwinOrderRev.reduce((a, b) => a + b, 0) / 5;

              const redpickOrderRev = [
                record.graphData[7],
                record.graphData[8],
                record.graphData[11],
                record.graphData[17],
                record.graphData[18],
              ].map((turn: any) => {
                if (turn) {
                  return turn.pickrate;
                }
                return 0;
              });
              redSta = redpickOrderRev.reduce((a, b) => a + b, 0) / 5;

              const bluewinOrderRev = [
                record.graphData[6],
                record.graphData[9],
                record.graphData[10],
                record.graphData[16],
                record.graphData[19],
              ].map((turn: any) => {
                if (turn) {
                  return turn.winrate;
                }
                return 0;
              });
              bluebanpickTotalScore =
                bluewinOrderRev.reduce((a, b) => a + b, 0) / 5;

              const bluepickOrderRev = [
                record.graphData[6],
                record.graphData[9],
                record.graphData[10],
                record.graphData[16],
                record.graphData[19],
              ].map((turn: any) => {
                if (turn) {
                  return turn.pickrate;
                }
                return 0;
              });
              blueSta = bluepickOrderRev.reduce((a, b) => a + b, 0) / 5;
            }

            return (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                }}
              >
                <RecordContainer showDetails={true}>
                  <TitleContainer>
                    <TitleText>GLPT Result</TitleText>
                    <DateText>{record?.date}</DateText>
                  </TitleContainer>
                  <TeamContainer showDetails={true}>
                    <TeamSection>
                      {record
                        ?.players!.slice(0, 5)
                        .map((player: any, index: number) => (
                          <PlayerContainer key={index}>
                            <PlayerCard>
                              <PlayerImage
                                url={player.championImage || '/pProfile.png'}
                              />
                              <PlayerName>{player.name}</PlayerName>
                            </PlayerCard>
                          </PlayerContainer>
                        ))}
                    </TeamSection>

                    <CenterVersusText>vs</CenterVersusText>
                    <TeamSection>
                      {record
                        ?.players!.slice(5, 10)
                        .map((player: any, index: number) => (
                          <PlayerContainer key={index}>
                            <PlayerCard>
                              <PlayerImage
                                url={player.championImage || '/pProfile.png'}
                              />
                              <PlayerName>{player.name}</PlayerName>
                            </PlayerCard>
                          </PlayerContainer>
                        ))}
                    </TeamSection>
                  </TeamContainer>
                  <BorderDiv />
                  <BanDeatilSection>
                    {record?.metadata || record?.teamMetadata ? (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '3rem',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            width: '100%',
                            gap: '2rem',
                          }}
                        >
                          <LinePlot data={{ data: record?.graphData }} />
                          <TeamMetaChart data={record?.teamMetadata} />
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              width: '20%',
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'row',
                                width: '100%',
                              }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  color: '#ffffff',
                                  fontSize: '0.8rem',
                                  textAlign: 'center',
                                }}
                              >
                                <OrderTitle>Red GLPT Score</OrderTitle>
                                <div style={{ height: '5px' }}></div>
                                <WinRate isRed={true}>
                                  {(100 - redbanpickTotalScore).toFixed(1)}
                                </WinRate>
                              </div>
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  color: '#ffffff',
                                  fontSize: '0.8rem',
                                  textAlign: 'center',
                                }}
                              >
                                <OrderTitle>Red Stability</OrderTitle>
                                <div style={{ height: '5px' }}></div>
                                <WinRate isRed={true}>
                                  {redSta.toFixed(1)}
                                </WinRate>
                              </div>
                            </div>
                            <div style={{ height: '5px' }}></div>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'row',
                                width: '100%',
                              }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  color: '#ffffff',
                                  fontSize: '0.8rem',
                                  textAlign: 'center',
                                }}
                              >
                                <OrderTitle>Blue GLPT Score</OrderTitle>
                                <div style={{ height: '5px' }}></div>
                                <WinRate isRed={false}>
                                  {bluebanpickTotalScore.toFixed(1)}
                                </WinRate>
                              </div>
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  color: '#ffffff',
                                  fontSize: '0.8rem',
                                  textAlign: 'center',
                                }}
                              >
                                <OrderTitle>Blue Stability</OrderTitle>
                                <div style={{ height: '5px' }}></div>
                                <WinRate isRed={false}>
                                  {blueSta.toFixed(1)}
                                </WinRate>
                              </div>
                            </div>
                            <div style={{ height: '5px' }}></div>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                color: '#ffffff',
                                fontSize: '0.8rem',
                                textAlign: 'center',
                              }}
                            >
                              GLPT 스코어는 20턴의 밴픽을 종합한 숫자로,
                              높을수록 좋은 수치를 나타냅니다.
                            </div>
                          </div>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            width: '100%',
                            gap: '2rem',
                          }}
                        >
                          <DetailTable
                            score={bluebanpickTotalScore}
                            champions={record?.players}
                            data={record?.metadata}
                          />
                        </div>
                      </div>
                    ) : (
                      <div
                        style={{
                          height: '50vh',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <LoadingSpinner width={80} />
                      </div>
                    )}
                  </BanDeatilSection>
                </RecordContainer>
              </div>
            );
          })
        ) : loading ? (
          <LoadingSpinner width={80} />
        ) : (
          <div
            style={{
              fontFamily: 'Pretendard',
              fontSize: '1.5rem',
            }}
          >
            {t(`history.nodata`)}
          </div>
        )}

        {/* ✅ 페이지네이션 버튼 */}
        <PaginationContainer>
          <LoadMoreButton
            onClick={() => fetchRecords(page)}
            disabled={loading || !hasMore}
          >
            {loading
              ? '로딩 중...'
              : hasMore
              ? '더 보기'
              : '더 이상 데이터 없음'}
          </LoadMoreButton>
        </PaginationContainer>
      </ScrollBox>
    </div>
  );
};
export default BanpickRecordItem;
